import { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material'
import {
  Security as SecurityIcon,
  Info as InfoIcon
} from '@mui/icons-material'
import GenerateVerificationCode from './GenerateVerificationCode'
import MSPRelationshipsList from './MSPRelationshipsList'
import MSPStatusBanner from './MSPStatusBanner'
import { getMSPStatus } from '../services/mspService'

interface MSPAccessProps {
  tenantId?: string
}

export default function MSPAccess({ tenantId }: MSPAccessProps) {
  const [refreshKey, setRefreshKey] = useState(0)
  const [relationshipStatus, setRelationshipStatus] = useState<string | null>(null)
  const [mspCompanyName, setMspCompanyName] = useState<string | null>(null)

  // Load MSP status on mount and when refreshKey changes
  useEffect(() => {
    if (tenantId) {
      loadMSPStatus()
    }
  }, [tenantId, refreshKey])

  const loadMSPStatus = async () => {
    if (!tenantId) return
    try {
      const status = await getMSPStatus(tenantId)
      setRelationshipStatus(status.relationshipStatus)
      setMspCompanyName(status.mspCompanyName)
    } catch (err) {
      console.error('Failed to load MSP status:', err)
    }
  }

  const handleCodeGenerated = () => {
    // Refresh relationships list and status when code is generated
    setRefreshKey(prev => prev + 1)
  }

  // Show loading state if tenantId is not available yet
  if (!tenantId) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 400, gap: 2 }}>
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary">
            Loading MSP Access...
          </Typography>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SecurityIcon sx={{ fontSize: 40 }} color="primary" />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              MSP Access Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Grant and manage Managed Service Provider (MSP) access to your TC-Config portal
            </Typography>
          </Box>
        </Box>

        {/* Status Banner */}
        <MSPStatusBanner 
          relationshipStatus={relationshipStatus}
          mspCompanyName={mspCompanyName}
        />

        {/* Info Alert */}
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 4 }}>
          <Typography variant="body2" gutterBottom>
            <strong>How MSP Access Works:</strong>
          </Typography>
          <ol style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
            <li>
              <strong>Generate Verification Code:</strong> Create a unique code for your MSP partner
            </li>
            <li>
              <strong>Add DNS TXT Record:</strong> Add the verification code to your domain's DNS settings
            </li>
            <li>
              <strong>MSP Verification:</strong> Your MSP verifies the DNS record and establishes access
            </li>
            <li>
              <strong>Azure AD B2B:</strong> You'll receive a guest user invitation from your MSP
            </li>
            <li>
              <strong>Secure Access:</strong> MSP's only have the permissions that you grant them
            </li>
            <li>
              <strong>Revocable:</strong> You can revoke MSP access at any time
            </li>
          </ol>
        </Alert>

        {/* Generate Verification Code Section */}
        <Box mb={4}>
          <GenerateVerificationCode 
            tenantId={tenantId} 
            onCodeGenerated={handleCodeGenerated}
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* MSP Relationships Section */}
        <Box key={refreshKey}>
          <MSPRelationshipsList tenantId={tenantId} />
        </Box>

        {/* Additional Info */}
        <Paper sx={{ p: 3, mt: 4, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            Security & Privacy
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>What MSPs Can Access:</strong>
          </Typography>
          <ul style={{ marginTop: 0, paddingLeft: 20 }}>
            <li>View your deployment history and status</li>
            <li>View deployed policies and configurations (read-only)</li>
            <li>View certificate information</li>
            <li>View usage statistics</li>
          </ul>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
            <strong>What MSPs Cannot Access:</strong>
          </Typography>
          <ul style={{ marginTop: 0, paddingLeft: 20 }}>
            <li>Access your Azure AD credentials</li>
            <li>View or modify billing information</li>
            <li>Access other customers' data</li>
          </ul>
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>You're in control:</strong> MSP access requires DNS verification and can be revoked at any time. 
              All MSP actions are logged for audit purposes.
            </Typography>
          </Alert>
        </Paper>
      </Box>
    </Container>
  )
}
